'use client';

import React, {
  ReactNode,
  forwardRef,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';

import Image from 'next/image';

import * as Accordion from '@radix-ui/react-accordion';
import { motion, useInView } from 'framer-motion';

import { cn } from '@kit/ui/utils';

import GradientNoiseGlow from '../survey-builder/shared/gradient-noise-glow';
import { LandingIcons } from './icons';
import GlowingBadge from './ui/glowing-badge';

type AccordionItemProps = {
  children: React.ReactNode;
  className?: string;
} & Accordion.AccordionItemProps;

const AccordionItem = forwardRef<HTMLDivElement, AccordionItemProps>(
  ({ children, className, ...props }, forwardedRef) => (
    <Accordion.Item
      className={cn(
        'overflow-visible focus-within:relative focus-within:z-10',
        className,
      )}
      {...props}
      ref={forwardedRef}
    >
      {children}
    </Accordion.Item>
  ),
);
AccordionItem.displayName = 'AccordionItem';

type AccordionTriggerProps = {
  children: React.ReactNode;
  className?: string;
};

const AccordionTrigger = forwardRef<HTMLButtonElement, AccordionTriggerProps>(
  ({ children, className, ...props }, forwardedRef) => {
    const id = useId();
    return (
      <Accordion.Header className="flex">
        <Accordion.Trigger
          id={id}
          className={cn(
            'group flex flex-1 cursor-pointer items-center justify-between px-5 leading-none outline-none',
            className,
          )}
          {...props}
          ref={forwardedRef}
          role="button"
          aria-label="Toggle content"
          aria-controls={id}
        >
          {children}
        </Accordion.Trigger>
      </Accordion.Header>
    );
  },
);
AccordionTrigger.displayName = 'AccordionTrigger';

type AccordionContentProps = {
  children: ReactNode;
  className?: string;
} & Accordion.AccordionContentProps;

const AccordionContent = forwardRef<HTMLDivElement, AccordionContentProps>(
  ({ children, className, ...props }, forwardedRef) => (
    <Accordion.Content
      className={cn(
        'data-[state=closed]:animate-slide-up data-[state=open]:animate-slide-down overflow-hidden text-[15px] font-medium',
        className,
      )}
      {...props}
      ref={forwardedRef}
    >
      <div className="px-5 py-2">{children}</div>
    </Accordion.Content>
  ),
);
AccordionContent.displayName = 'AccordionContent';

export type FeaturesDataProps = {
  title: string;
  content: ReactNode;
  image?: string;
  imageWidth?: number;
  imageHeight?: number;
  video?: string;
  icon?: React.ReactNode;
};

type FeaturesProps = {
  id?: string;
  heading: string;
  subHeading?: string;
  collapseDelay?: number;
  ltr?: boolean;
  linePosition?: 'left' | 'right' | 'top' | 'bottom';
  data: FeaturesDataProps[];
};

export default function Features({
  id = 'features',
  heading,
  subHeading,
  collapseDelay = 5000,
  ltr = false,
  linePosition = 'left',
  data = [],
}: FeaturesProps) {
  const [currentIndex, setCurrentIndex] = useState<number>(-1);
  const carouselRef = useRef<HTMLUListElement>(null);
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    amount: 0.5,
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      if (isInView) {
        setCurrentIndex(0);
      } else {
        setCurrentIndex(-1);
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [isInView]);

  const scrollToIndex = (index: number) => {
    if (carouselRef.current) {
      const card = carouselRef.current.querySelectorAll('.card')[index];
      if (card) {
        const cardRect = card.getBoundingClientRect();
        const carouselRect = carouselRef.current.getBoundingClientRect();
        const offset =
          cardRect.left -
          carouselRect.left -
          (carouselRect.width - cardRect.width) / 2;

        carouselRef.current.scrollTo({
          left: carouselRef.current.scrollLeft + offset,
          behavior: 'smooth',
        });
      }
    }
  };

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentIndex((prevIndex) =>
        prevIndex !== undefined ? (prevIndex + 1) % data.length : 0,
      );
    }, collapseDelay);

    return () => clearInterval(timer);
  }, [collapseDelay, currentIndex, data.length]);

  useEffect(() => {
    const handleAutoScroll = () => {
      const nextIndex =
        (currentIndex !== undefined ? currentIndex + 1 : 0) % data.length;
      scrollToIndex(nextIndex);
    };

    const autoScrollTimer = setInterval(handleAutoScroll, collapseDelay);

    return () => clearInterval(autoScrollTimer);
  }, [collapseDelay, currentIndex, data.length]);

  useEffect(() => {
    const carousel = carouselRef.current;
    if (carousel) {
      const handleScroll = () => {
        const scrollLeft = carousel.scrollLeft;
        const cardWidth = carousel.querySelector('.card')?.clientWidth ?? 0;
        const newIndex = Math.min(
          Math.floor(scrollLeft / cardWidth),
          data.length - 1,
        );
        setCurrentIndex(newIndex);
      };

      carousel.addEventListener('scroll', handleScroll);
      return () => carousel.removeEventListener('scroll', handleScroll);
    }
  }, [data.length]);

  return (
    <section ref={ref} id={id} className="pb-16 lg:pb-0 lg:pt-3">
      <div className="mx-auto flex max-w-1440 flex-col gap-6 lg:flex-row lg:gap-0">
        <div
          className={`order-[0] flex px-6 py-16 lg:py-25 lg:pl-24 xl:pr-24 ${
            ltr ? 'order-2 lg:justify-end' : 'justify-start'
          }`}
        >
          <Accordion.Root
            className=""
            type="single"
            defaultValue={`item-${currentIndex}`}
            value={`item-${currentIndex}`}
            onValueChange={(value) =>
              setCurrentIndex(Number(value.split('-')[1]))
            }
          >
            <motion.div
              initial={{ opacity: 0, y: 100 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, ease: 'easeOut' }}
            >
              <div className="mb-5 flex justify-start lg:mb-4">
                <GlowingBadge>
                  <LandingIcons.features />
                  <span>How it Works</span>
                </GlowingBadge>
              </div>
              <h2 className="text-[32px] font-semibold leading-[1.2] text-[#e6e6e6] lg:text-5xl lg:leading-[1.3]">
                {heading}
              </h2>
              {subHeading ? (
                <p className="mb-15 mt-4 text-sm leading-5 tracking-[-0.1px] text-white/50 lg:mb-18 lg:mt-8 lg:text-base lg:leading-6">
                  {subHeading}
                </p>
              ) : null}
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 100 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, ease: 'easeOut' }}
            >
              {data.map((item, index) => (
                <AccordionItem
                  key={index}
                  className="relative mb-11 last:mb-0 lg:mb-12"
                  value={`item-${index}`}
                >
                  {(linePosition === 'left' || linePosition === 'right') &&
                  index !== data.length - 1 ? (
                    <div
                      className={`absolute bottom-0 top-0 mt-10 h-full w-0.5 overflow-visible rounded-lg bg-neutral-300/50 dark:bg-neutral-300/30 ${
                        linePosition === 'right'
                          ? 'left-auto right-0'
                          : 'left-[23px] right-auto'
                      }`}
                    >
                      <div
                        className={`absolute left-0 top-0 w-full ${
                          currentIndex === index ? 'h-full' : 'h-0'
                        } via-gradient origin-top bg-gradient-to-b from-black to-white transition-all ease-linear`}
                        style={{
                          transitionDuration:
                            currentIndex === index
                              ? `${collapseDelay}ms`
                              : '0s',
                        }}
                      ></div>
                    </div>
                  ) : null}

                  {linePosition === 'top' || linePosition === 'bottom' ? (
                    <div
                      className={`absolute left-0 right-0 h-0.5 w-full overflow-hidden rounded-lg bg-neutral-300/50 dark:bg-neutral-300/30 ${
                        linePosition === 'bottom' ? 'bottom-0' : 'top-0'
                      }`}
                    >
                      <div
                        className={`absolute left-0 ${
                          linePosition === 'bottom' ? 'bottom-0' : 'top-0'
                        } h-full ${
                          currentIndex === index ? 'w-full' : 'w-0'
                        } origin-left bg-primary transition-all ease-linear dark:bg-white`}
                        style={{
                          transitionDuration:
                            currentIndex === index
                              ? `${collapseDelay}ms`
                              : '0s',
                        }}
                      ></div>
                    </div>
                  ) : null}

                  <div className="relative flex items-start pl-12 sm:pl-16">
                    <div className="item-box absolute -left-2 flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-full bg-neutral-900">
                      <div className="icon-container flex h-[46px] w-[46px] items-center justify-center">
                        <GradientNoiseGlow
                          className="relative flex h-full flex-1 items-center justify-center"
                          visible={currentIndex === index}
                          size="small"
                        >
                          {item.icon}
                        </GradientNoiseGlow>
                      </div>
                    </div>

                    <div>
                      <AccordionTrigger
                        className={cn(
                          'pl-6 pt-2 text-xl font-bold transition-colors duration-200 lg:pl-4 lg:text-[26px]',
                          {
                            'text-[#e6e6e6]': currentIndex === index,
                            'text-white/40': currentIndex !== index,
                          },
                        )}
                      >
                        {item.title}
                      </AccordionTrigger>

                      <AccordionTrigger
                        className={cn(
                          'mt-3 justify-start pl-6 pr-0 text-left text-sm leading-5 transition-colors duration-200 lg:pl-4 lg:pr-10 lg:text-base lg:leading-6 lg:tracking-[-0.1px]',
                          currentIndex === index
                            ? 'text-white/50'
                            : 'text-white/20',
                        )}
                      >
                        {item.content}
                      </AccordionTrigger>
                    </div>
                  </div>
                </AccordionItem>
              ))}
            </motion.div>
          </Accordion.Root>
        </div>
        <motion.div
          className={`h-[419px] min-h-[200px] w-full px-6 lg:mt-[200px] lg:h-[530px] lg:w-auto lg:flex-[0_0_500px] lg:px-0 xl:my-[130px] xl:mr-20 xl:h-[679px] xl:flex-[0_0_639px] ${
            ltr && 'lg:order-1'
          }`}
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.98 }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
        >
          {data[currentIndex]?.image ? (
            <motion.div
              key={currentIndex}
              className="aspect-auto h-full w-full overflow-hidden rounded-xl shadow-lg"
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.98 }}
              transition={{ duration: 0.25, ease: 'easeOut' }}
            >
              <Image
                src={data[currentIndex].image ?? ''}
                alt={data[currentIndex].title ?? 'feature image'}
                width={data[currentIndex].imageWidth ?? 640}
                height={data[currentIndex].imageHeight ?? 680}
                className="h-full w-full object-cover"
                priority={currentIndex === 0}
              />
            </motion.div>
          ) : data[currentIndex]?.video ? (
            <video
              key={currentIndex}
              preload="metadata"
              src={data[currentIndex].video}
              className="aspect-auto h-full w-full rounded-lg object-cover shadow-lg"
              autoPlay
              loop
              muted
            />
          ) : (
            <div
              key={currentIndex}
              className="aspect-auto h-full w-full rounded-xl bg-gray-200 p-1"
            ></div>
          )}
        </motion.div>
      </div>
    </section>
  );
}
